import { useState } from 'react';
import { Link } from 'react-router-dom';
import { locations } from '@/mocks/locations';

const mapColors: Record<string, string> = {
  middlesbrough: '#D4EDDA',
  durham: '#FDE8D8',
  hartlepool: '#E8F4F8',
  hull: '#FFF3CD',
  sheffield: '#F5E6FF',
};

const mapDots: Record<string, { x: number; y: number }> = {
  middlesbrough: { x: 62, y: 28 },
  durham:        { x: 57, y: 22 },
  hartlepool:    { x: 65, y: 25 },
  hull:          { x: 68, y: 48 },
  sheffield:     { x: 55, y: 58 },
};

export default function Locations() {
  const [active, setActive] = useState<string | null>(null);
  const activeLocation = locations.find((l) => l.id === active) ?? null;

  return (
    <section id="locations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <span className="inline-block bg-[#F8F7F4] text-[#1B4332] text-xs font-semibold px-4 py-1.5 rounded-full border border-stone-200 mb-4">
              Our Locations
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-stone-900 leading-tight" style={{ fontFamily: 'Syne, sans-serif' }}>
              Serving the North<br />
              of England
            </h2>
          </div>
          <p className="text-stone-500 text-sm leading-relaxed max-w-xs md:text-right">
            Five dedicated service areas — each with a local mobile tyre technician ready to come to you.
          </p>
        </div>

        {/* Main layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* Location cards — left */}
          <div className="lg:col-span-2 flex flex-col gap-3">
            {locations.map((loc) => (
              <div
                key={loc.id}
                onMouseEnter={() => setActive(loc.id)}
                onMouseLeave={() => setActive(null)}
                className={`group rounded-xl border transition-all duration-200 cursor-pointer p-4 ${
                  active === loc.id
                    ? 'border-[#1B4332] bg-[#F0FAF4]'
                    : 'border-stone-100 bg-[#FAFAF8] hover:border-[#1B4332]/40'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <span
                      className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5 text-[#1B4332]"
                      style={{ backgroundColor: mapColors[loc.id] ?? '#E8F5EE' }}
                    >
                      <i className="ri-map-pin-2-line text-base" />
                    </span>
                    <div>
                      <h3 className="text-sm font-bold text-stone-900 mb-0.5">{loc.city}</h3>
                      <p className="text-xs text-stone-500">{loc.address}, {loc.postcode}</p>
                      <a
                        href={`tel:${loc.phone}`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-xs text-[#1B4332] font-medium mt-1.5 hover:underline"
                      >
                        <i className="ri-phone-line" /> {loc.phoneFormatted}
                      </a>
                    </div>
                  </div>
                  <Link
                    to={`/locations/${loc.slug}`}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-stone-200 text-stone-400 hover:text-[#1B4332] hover:border-[#1B4332] transition-colors"
                    aria-label={`View ${loc.city} location`}
                  >
                    <i className="ri-arrow-right-line text-sm" />
                  </Link>
                </div>
              </div>
            ))}

            <Link
              to="/locations"
              className="mt-2 flex items-center justify-center gap-2 border border-[#1B4332] text-[#1B4332] text-sm font-semibold py-3 rounded-xl hover:bg-[#1B4332] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              View All Locations <i className="ri-arrow-right-line" />
            </Link>
          </div>

          {/* Map panel — right */}
          <div className="lg:col-span-3 relative rounded-2xl overflow-hidden bg-[#F0FAF4] border border-stone-100 min-h-[420px]">
            {/* Decorative UK outline background */}
            <div className="absolute inset-0 flex items-center justify-center opacity-5 pointer-events-none select-none">
              <i className="ri-map-2-line text-[#1B4332]" style={{ fontSize: '320px' }} />
            </div>

            {/* Embedded map for active location */}
            {activeLocation ? (
              <div className="absolute inset-0 transition-opacity duration-300">
                <iframe
                  key={activeLocation.id}
                  title={`Map of ${activeLocation.city}`}
                  src={activeLocation.googleMapsEmbed}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            ) : (
              /* Default state — dot map */
              <div className="absolute inset-0 flex flex-col items-center justify-center p-8">
                <div className="relative w-full max-w-[320px] aspect-[3/4]">
                  {/* Connecting lines */}
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 133" preserveAspectRatio="none">
                    {locations.map((loc, i) => {
                      if (i === 0) return null;
                      const prev = locations[i - 1];
                      const a = mapDots[loc.id];
                      const b = mapDots[prev.id];
                      return (
                        <line
                          key={loc.id}
                          x1={`${b.x}%`} y1={`${b.y}%`}
                          x2={`${a.x}%`} y2={`${a.y}%`}
                          stroke="#1B4332"
                          strokeWidth="0.5"
                          strokeDasharray="2 2"
                          opacity="0.3"
                        />
                      );
                    })}
                  </svg>

                  {/* Dots */}
                  {locations.map((loc) => {
                    const dot = mapDots[loc.id];
                    return (
                      <div
                        key={loc.id}
                        className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer group/dot"
                        style={{ left: `${dot.x}%`, top: `${dot.y}%` }}
                        onMouseEnter={() => setActive(loc.id)}
                        onMouseLeave={() => setActive(null)}
                      >
                        <div className="relative">
                          <div
                            className="w-4 h-4 rounded-full border-2 border-white flex items-center justify-center"
                            style={{ backgroundColor: mapColors[loc.id] ?? '#1B4332' }}
                          >
                            <div className="w-1.5 h-1.5 rounded-full bg-[#1B4332]" />
                          </div>
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-[#0F2A1D] text-white text-[10px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap opacity-0 group-hover/dot:opacity-100 transition-opacity pointer-events-none">
                            {loc.city}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <p className="text-stone-400 text-xs mt-6 text-center">
                  Hover a location card to see the map
                </p>
              </div>
            )}

            {/* Active location badge */}
            {activeLocation && (
              <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2.5 border border-stone-100">
                <p className="text-xs font-bold text-stone-900">{activeLocation.city}</p>
                <p className="text-[10px] text-stone-500">{activeLocation.address}, {activeLocation.postcode}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
